import type { ManagedTeacherUser } from '../models/managed-teacher-user';

export const INVITE_VALIDITY_DAYS = 7;
export const INVITE_EXPIRING_WINDOW_MS = 12 * 60 * 60 * 1000;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Whether the row can be deactivated (active teachers only).
 *
 * @param user
 */
export function canDisableUser(user: ManagedTeacherUser): boolean {
  return user.status === 'Active';
}

/**
 * Whether an invite email can be sent again for this invited user.
 *
 * @param user
 * @param nowMs
 */
export function canResendInvite(user: ManagedTeacherUser, nowMs: number = Date.now()): boolean {
  if (user.status !== 'Invited') {
    return false;
  }
  return isInviteExpired(user, nowMs) || user.lastLoginAt === null;
}

/**
 * Computes the invite expiry instant from the sent-at timestamp and {@link INVITE_VALIDITY_DAYS}.
 *
 * @param invitedAtIso
 */
export function inviteExpiresAtFromInvitedAt(invitedAtIso: string): string {
  const invitedAtMs = Date.parse(invitedAtIso);
  return new Date(invitedAtMs + INVITE_VALIDITY_DAYS * DAY_MS).toISOString();
}

/**
 * `Invited` user whose invite deadline has not passed yet.
 *
 * @param user
 * @param nowMs
 */
export function isActiveInvite(user: ManagedTeacherUser, nowMs: number = Date.now()): boolean {
  return user.status === 'Invited' && !isInviteExpired(user, nowMs);
}

/**
 * True when an invited user's invite deadline is in the past.
 *
 * @param user
 * @param nowMs
 */
export function isInviteExpired(user: ManagedTeacherUser, nowMs: number = Date.now()): boolean {
  if (user.status !== 'Invited') {
    return false;
  }
  return Date.parse(user.inviteExpiresAt) < nowMs;
}

/**
 * True when an invited user's invite expires within {@link INVITE_EXPIRING_WINDOW_MS}.
 *
 * @param user
 * @param nowMs
 */
export function isInviteExpiringSoon(user: ManagedTeacherUser, nowMs: number = Date.now()): boolean {
  if (user.status !== 'Invited' || isInviteExpired(user, nowMs)) {
    return false;
  }
  const expiresAtMs = Date.parse(user.inviteExpiresAt);
  return expiresAtMs <= nowMs + INVITE_EXPIRING_WINDOW_MS;
}
